import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import type { StagedSendConfig } from '@shared/flow/enrollment';
import { nonBlank } from '@shared/flow/non-blank';

/**
 * REP-LOCAL, BROWSER-PERSISTED staged play config — the play-level base copy
 * (and, from D12, the play-level sender + the "this row's sender was
 * hand-picked" set; and, from plan 2026-09-08-002 Part 2, per-step batch
 * defaults for `gong_flow` groups, `stepCopy`) a rep stages while reviewing a
 * queue batch.
 *
 * ── The one rule this module exists to enforce ───────────────────────────
 * NOTHING here ever reaches a server before Send. There is no endpoint, no
 * autosave, no debounce-to-network: a keystroke writes this store, zustand's
 * `persist` middleware rides that write through to localStorage, and that is
 * the WHOLE write path (design-fidelity plan D8 Part B, Spencer 2026-09-01
 * round 5: "we are not writing back to any dbs… only the rep and its
 * contacts and their state stores the staged copy while in queue", plus the
 * 2026-09-02 follow-up "if someone closes their tab they should still be
 * able to see the overridden changes"). At Send (terra-ohe.7, F1 U2/U6), this
 * store's contents ride the POST's `staged` wire field
 * (`buildStagedSendSnapshot` below, wired through
 * `EnrollQueueContext.buildStagedSnapshot`) and the SERVER resolves them onto
 * each non-overridden item's own `subject_draft`/`body_draft`/`sender` at
 * claim time — the only moment any of this becomes server state, and it
 * lands as ordinary per-item data. There is no client-side PATCH.
 *
 * The play-level `FlowPlayOverride.base_subject`/`base_body` columns, their
 * PUT route, the read-time overlay and the send-time `PlayContext` rung that
 * the first version of this feature shipped are all DELETED by D8 — one
 * rep's staging is structurally incapable of reaching another rep now
 * (localStorage is per-browser/per-origin AND the blob is identity-keyed).
 *
 * ── Precedent followed end to end ────────────────────────────────────────
 * `components/flow/zebraStripes.ts` is the repo's ONE existing
 * zustand-persist-to-localStorage store, and this file mirrors it exactly:
 * the curried `create<State>()(persist(...))` form (the only form CLAUDE.md
 * permits, and only because middleware is being composed), a
 * `createJSONStorage(() => localStorage)` storage, an exported `initialState`
 * so tests reset with `useStagedPlayConfigStore.setState(initialState, true)`,
 * and a module-scope `storage`-event listener that calls
 * `persist.rehydrate()` when ANOTHER TAB writes our key (cross-tab sync;
 * plan Open Question 8iii — two tabs of the same rep deliberately share
 * staged state, last-writer-wins at the persist layer, and the listener
 * means the losing tab immediately SHOWS the winning value).
 *
 * ── Identity keying is INSIDE the blob, on purpose ───────────────────────
 * `persist` needs one static storage `name`, so the rep cannot ride in the
 * storage key — `byIdentity` carries it instead. The key is the canonical
 * effective-identity composition the app already treats as authoritative:
 * `` `${userId}:${impersonation?.actingAsSlackId ?? 'self'}` `` from
 * `useUserControlsContext()`, byte-identical to `useSyncEffectiveIdentity.ts`'s.
 * Its resolved-identity guard is honored here too: callers pass `null` while
 * `userId` is null (`/api/me` unresolved, or a Clerk sessionId flap), reads
 * answer EMPTY and every mutator no-ops — nothing is ever written under an
 * unresolved key, and the flap can never migrate one rep's drafts onto
 * another key.
 *
 * ── Deliberately NOT registered with `lib/identity-scoped-reset.ts` ──────
 * That registry WIPES a store when the effective identity changes, which is
 * correct for a store holding ONE identity's data under no key. This store
 * is identity-keyed internally, so a wipe would destroy the OTHER identity's
 * persisted drafts — an impersonation switch here simply reads a different
 * bucket. This is a documented exception, not an oversight: do not "fix" it
 * by registering a reset.
 *
 * ── Plain objects/arrays, never Maps/Sets ────────────────────────────────
 * `persist`'s JSON serialization is the constraint. The `BaseCopyDraftStore`
 * facade built over this store in `EnrollQueueContext` re-exposes the
 * ergonomics consumers want; nothing outside this module reads the raw shape.
 * A corrupt blob or a version mismatch discards to `initialState` rather than
 * throwing (staged config is low-stakes and a bad blob must never crash the
 * dock), and a quota/private-mode STORAGE failure degrades to in-memory-only
 * — see `resilientJsonStorage` below for why that needed building rather
 * than assuming (Codex adversarial review, D8 regression #4: zustand does
 * NOT swallow storage errors, contrary to the original claim here).
 */

/** `${userId}:${impersonation?.actingAsSlackId ?? 'self'}` — see the doc comment. */
export type IdentityKey = string;
/** `${playTypeId}:${playInstanceRef}` — the composite play identity used as `PlayQueueCard`'s card key. */
export type PlayKey = string;

export interface StagedBaseCopy {
  subject: string;
  body: string;
}

/**
 * Plan 2026-09-08-002 Part 2, U5/Key Technical Decision 1 — one Gong step's
 * batch-wide default copy. `stepType` rides along because the PUT route
 * requires it (`StepDraftWriteSchema`), mirroring `StepOverrideDraft`'s own
 * shape (`state/step-override-drafts.ts`). Plain JSON object, like every
 * other field this module persists — inner keys are `String(stepNumber)`
 * (JSON object keys are strings).
 */
export interface StagedStepCopy {
  stepType: string;
  subject: string;
  body: string;
}

export interface StagedIdentityConfig {
  /** Play-level base subject/body, staged but never persisted server-side. */
  baseCopy: Record<PlayKey, StagedBaseCopy>;
  /**
   * D12 — the play-level sender selection (a `SenderSelectionSchema` value
   * like `'self'`/`'owner'`, never a resolved email). An entry exists only
   * once the rep actually picks.
   */
  baseSender: Record<PlayKey, string>;
  /**
   * D12 — item ids whose sender the rep hand-picked, so a staged play-level
   * sender must NOT re-follow them. An array (not a Set) because `persist`
   * serializes to JSON.
   */
  senderTouched: number[];
  /**
   * Plan 2026-09-08-002 Part 2, U5 — batch-wide Gong step defaults, a sibling
   * of `baseCopy` one nesting deeper: `PlayKey` -> `String(stepNumber)` ->
   * the staged copy for that step. Never carries a `null`/reset entry —
   * "Use flow default" at the batch level REMOVES the entry outright
   * (Resolved Decision 8), because there is no persisted batch state to
   * DELETE the way a per-lead reset intent has.
   */
  stepCopy: Record<PlayKey, Record<string, StagedStepCopy>>;
}

export interface StagedPlayConfigState {
  byIdentity: Record<IdentityKey, StagedIdentityConfig>;
}

export const initialState: StagedPlayConfigState = { byIdentity: {} };

export const STAGED_PLAY_CONFIG_STORAGE_KEY = 'terra:flow-queue:staged-play-config';
export const STAGED_PLAY_CONFIG_VERSION = 1;

/**
 * Stable "nothing staged" bucket. Stable IDENTITY matters: `stagedConfigFor`
 * is called from inside zustand selectors, and a fresh object per call would
 * make `useSyncExternalStore` see a changed snapshot on every render.
 */
export const EMPTY_STAGED_CONFIG: StagedIdentityConfig = { baseCopy: {}, baseSender: {}, senderTouched: [], stepCopy: {} };

export function playConfigKey(playTypeId: number, playInstanceRef: string): PlayKey {
  return `${playTypeId}:${playInstanceRef}`;
}

function isStagedBaseCopy(value: unknown): value is StagedBaseCopy {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.subject === 'string' && typeof record.body === 'string';
}

function isStagedStepCopy(value: unknown): value is StagedStepCopy {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.stepType === 'string' && typeof record.subject === 'string' && typeof record.body === 'string';
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Shape validation for whatever came out of localStorage. Returns `null` for
 * anything that isn't the exact expected shape, which the `merge` below turns
 * into a discard-to-`initialState`. Never throws — see the doc comment.
 */
function parseStagedState(persisted: unknown): StagedPlayConfigState | null {
  if (!isPlainRecord(persisted)) return null;
  const { byIdentity } = persisted;
  if (!isPlainRecord(byIdentity)) return null;

  const next: Record<IdentityKey, StagedIdentityConfig> = {};
  for (const [identity, bucket] of Object.entries(byIdentity)) {
    if (!isPlainRecord(bucket)) return null;
    if (!isPlainRecord(bucket.baseCopy) || !isPlainRecord(bucket.baseSender)) return null;
    if (!Array.isArray(bucket.senderTouched)) return null;

    const baseCopy: Record<PlayKey, StagedBaseCopy> = {};
    for (const [playKey, copy] of Object.entries(bucket.baseCopy)) {
      if (!isStagedBaseCopy(copy)) return null;
      baseCopy[playKey] = { subject: copy.subject, body: copy.body };
    }
    const baseSender: Record<PlayKey, string> = {};
    for (const [playKey, sender] of Object.entries(bucket.baseSender)) {
      if (typeof sender !== 'string') return null;
      baseSender[playKey] = sender;
    }
    if (bucket.senderTouched.some((id) => typeof id !== 'number')) return null;

    // U5 (plan 2026-09-08-002 Part 2) — `stepCopy` is OPTIONAL on the
    // persisted blob: an old blob written before this field existed has no
    // key at all, and that must default to `{}` rather than discard the
    // whole bucket (no `STAGED_PLAY_CONFIG_VERSION` bump — see the module
    // header). A PRESENT-but-malformed `stepCopy` still discards to
    // `initialState`, the same posture every other field here takes.
    //
    // U3 (gong-bulk-step-defaults-bugfix plan) — a step key must be exactly
    // `String(stepNumber)` for a positive integer `stepNumber` (the only
    // shape this module's own mutators ever write, `setStagedStepCopy`'s
    // `String(stepNumber)` key). Anything else — a hand-edited blob, a
    // future schema mistake — is malformed; discards to `initialState` the
    // same as every other present-but-wrong field here.
    const stepCopy: Record<PlayKey, Record<string, StagedStepCopy>> = {};
    if (bucket.stepCopy !== undefined) {
      if (!isPlainRecord(bucket.stepCopy)) return null;
      for (const [playKey, steps] of Object.entries(bucket.stepCopy)) {
        if (!isPlainRecord(steps)) return null;
        const stepsOut: Record<string, StagedStepCopy> = {};
        for (const [stepKey, copy] of Object.entries(steps)) {
          if (!/^[1-9]\d*$/.test(stepKey)) return null;
          if (!isStagedStepCopy(copy)) return null;
          stepsOut[stepKey] = { stepType: copy.stepType, subject: copy.subject, body: copy.body };
        }
        stepCopy[playKey] = stepsOut;
      }
    }

    next[identity] = { baseCopy, baseSender, senderTouched: [...(bucket.senderTouched as number[])], stepCopy };
  }
  return { byIdentity: next };
}

/**
 * In-memory fallback bucket — the whole "degrades to in-memory-only" promise.
 * Module-scope (not per-call) so a blob written after `localStorage` started
 * failing is still readable by a later `getItem` in the same session.
 */
const memoryStorage = new Map<string, string>();

/**
 * Codex adversarial review, D8 regression #4 — the storage adapter zustand
 * gets, hardened at BOTH of the two places the installed zustand (5.0.12)
 * genuinely throws or degrades badly:
 *
 * 1. **`setItem` throwing synchronously from a keystroke.**
 *    `createJSONStorage`'s `setItem` is a bare `storage.setItem(...)` with no
 *    try/catch (`node_modules/zustand/esm/middleware.mjs`), and `persistImpl`
 *    overrides `api.setState` to call it SYNCHRONOUSLY. So a
 *    `QuotaExceededError` (disk full, or Safari private mode, where
 *    `localStorage` exists but every write throws) propagated straight out of
 *    `setStagedBaseCopy` → out of `BaseCopyEditor`'s `onChange` → crash on a
 *    keystroke. Every method here catches and falls back to `memoryStorage`.
 *
 * 2. **`localStorage` being unavailable at STORE-CREATION time.**
 *    `createJSONStorage(getStorage)` returns `undefined` when `getStorage()`
 *    throws (e.g. a `SecurityError` on the property access itself), and
 *    `persistImpl` then takes an early `if (!storage) return config(...)`
 *    path that never attaches `api.persist` AT ALL — so
 *    `useStagedPlayConfigStore.persist.hasHydrated()` would throw on mount.
 *    This factory never throws and never returns undefined, so `persist` is
 *    always attached. `stagedConfigHasHydrated`/`rehydrateStagedConfig`
 *    below are the belt to that braces: every call site goes through them,
 *    so nothing outside this module dereferences `.persist` unguarded.
 *
 * Values are strings (`StateStorage`); `createJSONStorage` layers the
 * JSON.parse/stringify on top, so `getItem` returning `null` is the "nothing
 * stored" signal it already handles.
 */
function resilientJsonStorage(): StateStorage {
  const browserStorage = (): Storage | null => {
    try {
      return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      return null;
    }
  };
  return {
    getItem: (name) => {
      try {
        const value = browserStorage()?.getItem(name);
        // A real `null` from a WORKING storage means "nothing stored" — but
        // fall through to memory anyway, because a degraded session's only
        // copy lives there. `undefined` means there is no browser storage.
        if (value != null) return value;
      } catch {
        // fall through to memory
      }
      return memoryStorage.get(name) ?? null;
    },
    setItem: (name, value) => {
      try {
        const storage = browserStorage();
        if (storage) {
          storage.setItem(name, value);
          // Durable copy landed — drop any stale memory-only copy so the two
          // can never disagree on a later read.
          memoryStorage.delete(name);
          return;
        }
      } catch {
        // Quota exceeded / private mode — fall through to memory-only.
      }
      memoryStorage.set(name, value);
    },
    removeItem: (name) => {
      memoryStorage.delete(name);
      try {
        browserStorage()?.removeItem(name);
      } catch {
        // Nothing to do — the memory copy is already gone.
      }
    },
  };
}

export const useStagedPlayConfigStore = create<StagedPlayConfigState>()(
  persist(
    () => ({
      ...initialState,
    }),
    {
      name: STAGED_PLAY_CONFIG_STORAGE_KEY,
      version: STAGED_PLAY_CONFIG_VERSION,
      storage: createJSONStorage(resilientJsonStorage),
      // A version bump means "the shape changed" — discard rather than
      // guess. Staged config is one review session's worth of unsent text;
      // there is nothing here worth a migration path.
      migrate: () => ({ ...initialState }),
      merge: (persisted, current) => {
        const parsed = parseStagedState(persisted);
        return parsed ?? { ...current, ...initialState };
      },
    },
  ),
);

/**
 * Codex adversarial review, D8 regression #4 — the ONE guarded view of
 * zustand's `persist` API, so no consumer ever dereferences `.persist`
 * unconditionally. zustand types `persist` as always-present, but
 * `persistImpl` genuinely returns WITHOUT attaching it when it cannot
 * construct a storage (`if (!storage) return config(...)`). The resilient
 * adapter above makes that unreachable for this store, but "unreachable
 * today" is not a thing to build a crash-on-mount on top of. Widened
 * structurally (no cast) so the optional chaining below is real rather than
 * a lint-suppressed no-op against a non-optional type.
 */
const persistApi: {
  persist?: {
    hasHydrated?: () => boolean;
    rehydrate?: () => Promise<void> | void;
  };
} = useStagedPlayConfigStore;

/**
 * "Has the persisted blob been read back yet?" — the gate the queue's
 * staged-config prune waits on before doing anything destructive. `true`
 * when there is no persistence at all: there is then no hydration to wait
 * for, and blocking forever would silently disable the prune.
 */
export function stagedConfigHasHydrated(): boolean {
  return persistApi.persist?.hasHydrated?.() ?? true;
}

/**
 * Re-read the persisted blob (the cross-tab `storage`-event path below, and
 * tests). Resolves — never rejects and never throws — with no persistence.
 */
export async function rehydrateStagedConfig(): Promise<void> {
  try {
    await persistApi.persist?.rehydrate?.();
  } catch {
    // Same posture as the corrupt-blob discard: a failed re-read leaves the
    // in-memory state exactly as it was. Never a crash, never a rejection
    // the `storage`-event listener below would have to handle.
  }
}

/** Read one identity's bucket off a state snapshot; EMPTY for null/unknown. */
export function stagedConfigFor(
  state: StagedPlayConfigState,
  identity: IdentityKey | null,
): StagedIdentityConfig {
  if (identity === null) return EMPTY_STAGED_CONFIG;
  return state.byIdentity[identity] ?? EMPTY_STAGED_CONFIG;
}

/**
 * NON-REACTIVE read of one identity's staged bucket — the read path of the
 * identity-stable `BaseCopyDraftStore` facade, and of `buildStagedSendSnapshot`
 * below.
 *
 * D24 (qa-round3 plan, fix 1). Both used to close over a RENDERED bucket from
 * a `useStagedConfig(identity)` subscription held by `EnrollQueueProvider`
 * itself. Because every mutator mints a fresh bucket (`updateBucket`), that
 * selector changed identity on every keystroke, which re-rendered the
 * 2400-line provider, re-memoized the facade, minted a new context value and
 * — context propagation bypasses React's `children`-identity bailout —
 * re-rendered all 16 `useEnrollQueue()` consumers per character, including
 * per-contact-table-row components with O(queue-size) scans on a surface
 * unrelated to base copy.
 *
 * Reading through `getState()` instead makes the facade memoizable on
 * `[stagedIdentity]` alone, and is strictly FRESHER than the closed-over
 * value: it answers the store as it is at CALL time, so a facade built one
 * render ago can never hand back a stale bucket. The reactivity consumers
 * genuinely need is theirs to declare, narrowly, via the hooks below.
 */
export function readStagedConfig(identity: IdentityKey | null): StagedIdentityConfig {
  return stagedConfigFor(useStagedPlayConfigStore.getState(), identity);
}

/**
 * ── The narrow per-field subscriptions (D24 fix 1) ──────────────────────────
 * CLAUDE.md's "narrow per-field selectors" zustand shape, and the replacement
 * for the whole-bucket `useStagedConfig` this module used to export. Each
 * returns a value whose identity changes ONLY when that exact field changes,
 * so `Object.is` (zustand's default snapshot comparison) does the work:
 * - a `baseCopy` write does not notify a `baseSender`/`senderTouched`
 *   subscriber, and vice versa (`updateBucket` spreads, so untouched fields
 *   keep their reference), and
 * - play A's subscriber is untouched by a write to play B.
 *
 * Deliberately NOT one `useStagedPlay(identity, playKey)` returning a
 * composite: that would have to allocate, and an allocating selector is the
 * exact `useSyncExternalStore` footgun `EMPTY_STAGED_CONFIG` exists to avoid.
 */
export function useStagedBaseCopy(
  identity: IdentityKey | null,
  playKey: PlayKey,
): StagedBaseCopy | undefined {
  return useStagedPlayConfigStore((s) => stagedConfigFor(s, identity).baseCopy[playKey]);
}

export function useStagedBaseSender(
  identity: IdentityKey | null,
  playKey: PlayKey,
): string | undefined {
  return useStagedPlayConfigStore((s) => stagedConfigFor(s, identity).baseSender[playKey]);
}

/**
 * The `senderTouched` ARRAY, for a consumer that has to answer "is this row
 * touched?" for many rows in one render (`PlayQueueCard` maps over all of its
 * group's items, so a per-item hook is not available to it). The array
 * reference is stable across `baseCopy`/`baseSender` writes, which is what
 * makes this narrow.
 *
 * D24 flag, deliberately NOT fixed here (out of this unit's scope): callers
 * still answer per row with an O(n) `.includes` scan, so a card render is
 * O(items x touched). It wants to be a memoized `Set` at this boundary
 * (`persist` needs the array for JSON, the facade does not) — but it is a
 * pre-existing cost, and it stops being a PER-KEYSTROKE cost the moment this
 * unit's fix 1 lands, which is the part that mattered.
 */
export function useStagedSenderTouched(identity: IdentityKey | null): readonly number[] {
  return useStagedPlayConfigStore((s) => stagedConfigFor(s, identity).senderTouched);
}

/** Single-row form of `useStagedSenderTouched` — a boolean, so a pin landing on
 *  ANOTHER row never re-renders this one. */
export function useIsSenderTouched(identity: IdentityKey | null, itemId: number): boolean {
  return useStagedPlayConfigStore((s) => stagedConfigFor(s, identity).senderTouched.includes(itemId));
}

/**
 * Plan 2026-09-08-002 Part 2, Key Technical Decision 1 — `useStagedBaseCopy`'s
 * sibling for batch step defaults: narrow to ONE play's `stepCopy` record, so
 * a write to another play's step copy, or to `baseCopy`/`baseSender`, never
 * notifies this subscriber. `PlayQueueCard` reads this once per card and
 * threads the record down as a plain data prop (`batchStepCopy`) — see
 * `StepOverrideEditor.tsx`'s prop of the same name.
 */
export function useStagedStepCopy(
  identity: IdentityKey | null,
  playKey: PlayKey,
): Readonly<Record<string, StagedStepCopy>> | undefined {
  return useStagedPlayConfigStore((s) => stagedConfigFor(s, identity).stepCopy[playKey]);
}

/**
 * Copy-on-write update of ONE identity's bucket. No-ops (returning the same
 * state object, so no subscriber notification) for an unresolved identity or
 * when the updater reports no change.
 */
function updateBucket(
  identity: IdentityKey | null,
  update: (bucket: StagedIdentityConfig) => StagedIdentityConfig | null,
): void {
  if (identity === null) return;
  useStagedPlayConfigStore.setState((state) => {
    const next = update(state.byIdentity[identity] ?? EMPTY_STAGED_CONFIG);
    if (next === null) return state;
    return { byIdentity: { ...state.byIdentity, [identity]: next } };
  });
}

export function setStagedBaseCopy(identity: IdentityKey | null, playKey: PlayKey, draft: StagedBaseCopy): void {
  updateBucket(identity, (bucket) => ({
    ...bucket,
    baseCopy: { ...bucket.baseCopy, [playKey]: { subject: draft.subject, body: draft.body } },
  }));
}

/** D12's half — staged here now so the store shape is settled in one place. */
export function setStagedBaseSender(identity: IdentityKey | null, playKey: PlayKey, sender: string): void {
  updateBucket(identity, (bucket) => ({
    ...bucket,
    baseSender: { ...bucket.baseSender, [playKey]: sender },
  }));
}

/** D12's half — idempotent, so a repeat pick doesn't grow the array. */
export function markSenderTouched(identity: IdentityKey | null, itemId: number): void {
  updateBucket(identity, (bucket) =>
    bucket.senderTouched.includes(itemId)
      ? null
      : { ...bucket, senderTouched: [...bucket.senderTouched, itemId] },
  );
}

/** D12's half — "this row follows the play config again" (the reset path). */
export function clearSenderTouched(identity: IdentityKey | null, itemId: number): void {
  updateBucket(identity, (bucket) =>
    bucket.senderTouched.includes(itemId)
      ? { ...bucket, senderTouched: bucket.senderTouched.filter((id) => id !== itemId) }
      : null,
  );
}

/**
 * Plan 2026-09-08-002 Part 2, Key Technical Decision 1 — the batch step
 * editor's write path (via `BulkStepDefaultsEditor`'s typing buffer, never
 * per keystroke — see `BASE_COPY_COMMIT_MS`'s sibling in that file).
 * Copy-on-write, spreading the play's OTHER staged steps untouched, so a
 * write to step 2 never changes the reference of step 1's entry (the same
 * "untouched fields keep their reference" property every other mutator here
 * documents).
 */
export function setStagedStepCopy(
  identity: IdentityKey | null,
  playKey: PlayKey,
  stepNumber: number,
  draft: StagedStepCopy,
): void {
  updateBucket(identity, (bucket) => ({
    ...bucket,
    stepCopy: {
      ...bucket.stepCopy,
      [playKey]: {
        ...bucket.stepCopy[playKey],
        [String(stepNumber)]: { stepType: draft.stepType, subject: draft.subject, body: draft.body },
      },
    },
  }));
}

/**
 * Plan 2026-09-08-002 Part 2, Resolved Decision 8 — the batch editor's "Use
 * flow default": removes the step's staged entry OUTRIGHT rather than
 * recording a `values: null` reset intent, because there is no persisted
 * batch state to DELETE. Removes the play's whole `stepCopy` entry once its
 * last step is cleared, mirroring `reconcileStagedPlayConfig`'s own
 * never-leave-an-empty-bucket posture. Same-identity no-op when the step has
 * no staged entry.
 */
export function clearStagedStepCopy(identity: IdentityKey | null, playKey: PlayKey, stepNumber: number): void {
  updateBucket(identity, (bucket) => {
    const steps = bucket.stepCopy[playKey];
    const key = String(stepNumber);
    if (!steps || !(key in steps)) return null;
    const nextSteps = { ...steps };
    delete nextSteps[key];
    const nextStepCopy = { ...bucket.stepCopy };
    if (Object.keys(nextSteps).length === 0) {
      delete nextStepCopy[playKey];
    } else {
      nextStepCopy[playKey] = nextSteps;
    }
    return { ...bucket, stepCopy: nextStepCopy };
  });
}

/**
 * U3 (gong-bulk-step-defaults-bugfix plan, MUST-ADDRESS) — the batch
 * editor's mount-time prune against the flow's LIVE steps
 * (`BulkStepDefaultsEditor`'s `useGongFlowStepsForced` fetch, which force-
 * refetches on every mount). A staged entry for a step Gong-side deleted or
 * renumbered upstream is invisible in the batch editor (it renders only
 * live steps) but, without this, still survives in the staged store and
 * materializes into a PUT the send-time guard rejects — with no rep-visible
 * way to clear it, since the batch editor shows nothing to reset. Dropping
 * (never erroring) is correct: the staged value refers to a step that no
 * longer exists, so there is nothing meaningful left to send.
 *
 * Drops any staged entry whose `step_order` has no live step, or whose live
 * `step_type` no longer matches the staged one — the identical match rule
 * `deriveStagedStepWrites` (`bulk-step-defaults.ts`) uses at send time, so
 * the batch editor and the send-time guard can never disagree about which
 * staged entries are still meaningful. Empties the play's whole `stepCopy`
 * entry once its last step is dropped, mirroring `clearStagedStepCopy`'s own
 * never-leave-an-empty-record posture. Same-identity no-op when nothing
 * changes.
 */
export function reconcileStagedStepCopy(
  identity: IdentityKey | null,
  playKey: PlayKey,
  liveSteps: ReadonlyArray<{ step_order: number; step_type: string }>,
): void {
  updateBucket(identity, (bucket) => {
    const steps = bucket.stepCopy[playKey];
    if (!steps) return null;
    const liveTypeByStepOrder = new Map(liveSteps.map(step => [step.step_order, step.step_type]));
    const nextSteps: Record<string, StagedStepCopy> = {};
    for (const [stepKey, copy] of Object.entries(steps)) {
      if (liveTypeByStepOrder.get(Number(stepKey)) === copy.stepType) {
        nextSteps[stepKey] = copy;
      }
    }
    if (Object.keys(nextSteps).length === Object.keys(steps).length) return null;
    const nextStepCopy = { ...bucket.stepCopy };
    if (Object.keys(nextSteps).length === 0) {
      delete nextStepCopy[playKey];
    } else {
      nextStepCopy[playKey] = nextSteps;
    }
    return { ...bucket, stepCopy: nextStepCopy };
  });
}

/**
 * The post-send prune (D8 Part B point 4) — now that staged config persists,
 * it must be pruned rather than abandoned:
 * - `senderTouched` drops every item id no longer open in the queue (sent
 *   items leave it), and
 * - a play's `baseCopy`/`baseSender` entries clear ONLY when the play has no
 *   remaining open items ("on send this work is lost" applies to the
 *   completed batch). A PARTIAL send — a row-level Send, or blocked
 *   leftovers — deliberately KEEPS the play's staged config so the remaining
 *   items still display and send with it.
 *
 * Hygiene + semantics, NOT correctness: a stale staged entry cannot clobber
 * a materialized item, because per-item values beat the staged rung in every
 * derivation (`item.subject_draft ?? staged ?? campaign`). Runs against the
 * queue as it actually is, so it also reconciles entries orphaned by a send
 * completed in another tab or a prior session.
 */
export function reconcileStagedPlayConfig(
  identity: IdentityKey | null,
  open: { openItemIds: ReadonlySet<number>; openPlayKeys: ReadonlySet<PlayKey> },
): void {
  if (identity === null) return;
  // Never CREATE a bucket for an identity that has nothing staged — an empty
  // bucket would be a pointless persisted write on every queue load.
  if (useStagedPlayConfigStore.getState().byIdentity[identity] === undefined) return;

  updateBucket(identity, (bucket) => {
    const senderTouched = bucket.senderTouched.filter((id) => open.openItemIds.has(id));
    const baseCopyKeys = Object.keys(bucket.baseCopy).filter((key) => open.openPlayKeys.has(key));
    const baseSenderKeys = Object.keys(bucket.baseSender).filter((key) => open.openPlayKeys.has(key));
    // U5 (plan 2026-09-08-002 Part 2) — `stepCopy` prunes by the SAME
    // openPlayKeys rule as `baseCopy`/`baseSender`: a play with no remaining
    // open items loses its whole batch step-defaults entry too.
    const stepCopyKeys = Object.keys(bucket.stepCopy).filter((key) => open.openPlayKeys.has(key));
    const unchanged =
      senderTouched.length === bucket.senderTouched.length
      && baseCopyKeys.length === Object.keys(bucket.baseCopy).length
      && baseSenderKeys.length === Object.keys(bucket.baseSender).length
      && stepCopyKeys.length === Object.keys(bucket.stepCopy).length;
    if (unchanged) return null;

    const baseCopy: Record<PlayKey, StagedBaseCopy> = {};
    for (const key of baseCopyKeys) baseCopy[key] = bucket.baseCopy[key];
    const baseSender: Record<PlayKey, string> = {};
    for (const key of baseSenderKeys) baseSender[key] = bucket.baseSender[key];
    const stepCopy: Record<PlayKey, Record<string, StagedStepCopy>> = {};
    for (const key of stepCopyKeys) stepCopy[key] = bucket.stepCopy[key];
    return { baseCopy, baseSender, senderTouched, stepCopy };
  });
}

/**
 * The DISPLAY rung (D8 Part B point 3) — the staged base copy sits between
 * the item's own draft and the wire campaign copy: a non-blank staged field
 * wins, a blank one falls through to the campaign copy (which is exactly what
 * `BaseCopyEditor`'s "Using campaign subject"/"Using campaign copy"
 * placeholders promise — D20 moved the explanation from a helper-line
 * sentence to per-field placeholders, the underlying precedence is
 * unchanged). Per FIELD, never per row.
 *
 * Used for both the per-item prefill (`copyPrefill`) and the send-time
 * materialization, so the value a rep sees and the value that sends can never
 * be derived differently.
 */
export function mergeStagedCopyPrefill(
  staged: StagedBaseCopy | undefined,
  wire: { subject: string | null; body: string | null },
): { subject: string | null; body: string | null } {
  return {
    subject: nonBlank(staged?.subject) ?? wire.subject,
    body: nonBlank(staged?.body) ?? wire.body,
  };
}

/**
 * F1 U6 (DBOS durable-send migration) — the wire-shape builder for `POST
 * /api/flow/queue/send`'s optional `staged` field (`StagedSendConfigSchema`,
 * U2). Replaces the deleted client-side send-time materialization function:
 * since U2, the server resolves the base_copy/base_sender/
 * sender_touched precedence itself when it claims each row, so this function
 * does no resolution at all — it just packages what THIS identity has staged
 * for THIS batch's plays into the wire shape.
 *
 * `itemIdsByPlayKey` is the batch's own shape: every play key the batch's
 * items belong to, mapped to the subset of `itemIds` on that play. Both
 * `base_copy`/`base_sender` are narrowed to those play keys (a play staged
 * outside this batch must never ride along), and `sender_touched` is narrowed
 * to the union of every id across those keys (a touched id outside the batch
 * is irrelevant to this send).
 *
 * Returns `undefined` — never an all-empty object — when nothing staged
 * applies to the batch, so callers omit `staged` from the POST body rather
 * than sending a pointless empty one (`SendSchema.staged` is optional for
 * exactly this reason).
 */
export function buildStagedSendSnapshot(
  identity: IdentityKey | null,
  itemIdsByPlayKey: Record<PlayKey, number[]>,
): StagedSendConfig | undefined {
  const bucket = readStagedConfig(identity);
  const batchItemIds = new Set<number>();
  for (const ids of Object.values(itemIdsByPlayKey)) {
    for (const id of ids) batchItemIds.add(id);
  }

  const base_copy: Record<PlayKey, StagedBaseCopy> = {};
  const base_sender: Record<PlayKey, string> = {};
  for (const playKey of Object.keys(itemIdsByPlayKey)) {
    const stagedCopy = bucket.baseCopy[playKey];
    if (stagedCopy !== undefined) base_copy[playKey] = stagedCopy;
    const stagedSender = bucket.baseSender[playKey];
    if (stagedSender !== undefined) base_sender[playKey] = stagedSender;
  }
  const sender_touched = bucket.senderTouched.filter((id) => batchItemIds.has(id));

  const hasAnything = Object.keys(base_copy).length > 0 || Object.keys(base_sender).length > 0 || sender_touched.length > 0;
  if (!hasAnything) return undefined;
  return { base_copy, base_sender, sender_touched };
}

// Cross-tab sync — the zebraStripes pattern verbatim (zustand's `persist`
// only updates the tab that wrote; the official docs' `storage`-event
// rehydrate is what makes another tab's write land here live rather than at
// next load). See the doc comment's Open Question 8iii note for the accepted
// last-writer-wins edge when two tabs stage the same play simultaneously.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e: StorageEvent) => {
    if (e.key === STAGED_PLAY_CONFIG_STORAGE_KEY) {
      // Guarded (regression #4): a store built without persistence has no
      // `.persist` at all, and this listener runs on every tab's write.
      void rehydrateStagedConfig();
    }
  });
}
